import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// The light at the four key moments of the adventure: dawn at the first
// question, noon, golden hour at the last question, and the twilight of the
// finale. `lit` and `shade` multiply the daytime material colours on sunlit
// and shadowed pixels; `haze` is the air colour distant layers fade into.
const KEYS = {
  sky0: ['#262a63', '#2557c4', '#33408f', '#0e0c2a'],
  sky1: ['#40438c', '#3274d9', '#5a58a8', '#1f1a4d'],
  sky2: ['#8463a6', '#4f99ea', '#b26b9c', '#4c2f7c'],
  sky3: ['#e48a9c', '#84c3f2', '#f29a70', '#a84a86'],
  sky4: ['#ffcda2', '#c8edf9', '#ffd68a', '#ff8a62'],
  haze: ['#a490c4', '#a8d2f0', '#eaa894', '#5c4a86'],
  lit: ['#f6d8e4', '#ffffff', '#ffe4bc', '#a08cca'],
  shade: ['#a39bd2', '#c6d2f2', '#bb98b8', '#5e5096'],
  sun: ['#ffe6c8', '#fffbe6', '#ffe8a8', '#ff9c6c'],
  glow: ['#f6a8ae', '#e2f5fc', '#ffbe7a', '#c84c6c'],
} as const;
/** How dark it is (windows lit, stars out) at each key. */
const NIGHT = [0.25, 0, 0.12, 1];

type KeyName = keyof typeof KEYS;
const KEY_NAMES = Object.keys(KEYS) as KeyName[];
const PARSED = KEY_NAMES.reduce(
  (all, name) => ({ ...all, [name]: KEYS[name].map(hexToRgb) }),
  {} as Record<KeyName, Rgb[]>,
);

export interface PixelLight {
  /** Sky bands from the zenith down to the horizon. */
  sky: [Rgb, Rgb, Rgb, Rgb, Rgb];
  haze: Rgb;
  lit: Rgb;
  shade: Rgb;
  sun: Rgb;
  glow: Rgb;
  /** 0 by day, 1 at twilight: lit windows, stars and fireflies. */
  night: number;
}

/** The light at a time of day: 0 dawn, 1/3 noon, 2/3 golden hour, 1 twilight. */
export const pixelLight = (daytime: number): PixelLight => {
  const scaled = clamp(daytime) * 3;
  const index = Math.min(2, Math.floor(scaled));
  const amount = smoothstep(0, 1, scaled - index);
  const pick = (name: KeyName) => mixRgb(PARSED[name][index], PARSED[name][index + 1], amount);
  return {
    sky: [pick('sky0'), pick('sky1'), pick('sky2'), pick('sky3'), pick('sky4')],
    haze: pick('haze'),
    lit: pick('lit'),
    shade: pick('shade'),
    sun: pick('sun'),
    glow: pick('glow'),
    night: NIGHT[index] + (NIGHT[index + 1] - NIGHT[index]) * amount,
  };
};

/** Dawn on the first question, golden hour on the last, twilight once the flow is done. */
export const pixelDaytime = (progress: number, finale: number) => clamp(progress) * (2 / 3) + clamp(finale) / 3;

// Daytime material colours, darkest first. Graded by `pixelLight` per layer.
export const MATERIALS = {
  cloud: ['#8f9bc8', '#c3cdec', '#e6ecfb', '#ffffff'],
  farBack: ['#7d8fc8', '#a7b6e4', '#c9d4f2', '#f4f7ff'],
  farFront: ['#4e5fa6', '#6d80c2', '#93a6dc', '#c4d1f2', '#f8fbff'],
  cliff: ['#1d2a3c', '#2c4256', '#41617a', '#64889a', '#9cc0c2'],
  grass: ['#1f5a3c', '#2f7d40', '#4ea446', '#7fca4e', '#c4ec6a'],
  leaf: ['#17452f', '#256b3a', '#3d9142', '#6cbd48', '#a8e05c'],
  dirt: ['#2a1a2c', '#4e2f36', '#774a3f', '#a06a4a', '#c99263'],
  water: ['#183a74', '#2460a8', '#3a8ed2', '#78c8f0', '#dcf7ff'],
  stone: ['#262338', '#4b4766', '#6f6b8c', '#9a96b4', '#c8c4dc', '#ece9f6'],
  roof: ['#1c2554', '#2b4592', '#3f6cc4', '#6fa2e6', '#a8d0f6'],
  flower: ['#ff6f9c', '#ffe066', '#ffffff', '#b38cff'],
  trunk: ['#3a2230', '#5e3a36'],
  window: ['#3a3458', '#ffd27a'],
  gold: ['#8a4b1a', '#d98a24', '#ffc93c', '#fff1a8'],
} as const;

// The hero: an adventurer in an orange hood and scarf.
export const HERO_COLORS: Record<string, string> = {
  o: '#2a1b3d',
  h: '#ff5a1f',
  H: '#ffa24c',
  d: '#b8321c',
  s: '#ffd6ac',
  S: '#e7a07c',
  e: '#2a1b3d',
  c: '#ff8f80',
  w: '#f7f0e2',
  W: '#c6b8b4',
  g: '#ffc93c',
  t: '#3b7fa0',
  T: '#24506e',
  b: '#7a4630',
};

/** Gem hues, cycled across the questions: [outline, dark, mid, light, highlight]. */
export const GEM_HUES: string[][] = [
  ['#5a0f2e', '#b51e4a', '#ff3d6e', '#ff9ab4', '#ffffff'],
  ['#5a2a06', '#c25a0e', '#ff9a1f', '#ffd27a', '#ffffff'],
  ['#0c4a2a', '#13894a', '#2fd673', '#a4f5bf', '#ffffff'],
  ['#0d2a66', '#1c5fd1', '#3fa2ff', '#a8dcff', '#ffffff'],
  ['#3a1466', '#7a2ccc', '#b46bff', '#e0c2ff', '#ffffff'],
  ['#0d4a52', '#139aa6', '#3fe0e6', '#b8fbff', '#ffffff'],
];

/** Firework colours: [hot core, bright, base, ember]. */
export const SPARK_HUES: string[][] = [
  ['#ffffff', '#ffd8a0', '#ff8a3a', '#a8341c'],
  ['#ffffff', '#fff2a0', '#ffd23a', '#a8701c'],
  ['#ffffff', '#ffc4e4', '#ff5fae', '#8e1f5c'],
  ['#ffffff', '#c4f6ff', '#4fd8ff', '#1c5f9e'],
  ['#ffffff', '#d8ffc0', '#7ff05a', '#2a7a2c'],
  ['#ffffff', '#e6d4ff', '#a98cff', '#4e2c9e'],
];

/** The sky stays deep blue to violet at the top all day, so the header text stays white. */
export const pixelChromeTone = (progress: number): ChromeTone =>
  luminance(pixelLight(pixelDaytime(progress, 0)).sky[0]) > 0.31 ? 'dark' : 'light';
